import React from 'react';
import { Head } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Link } from '@inertiajs/react';
import AdminLayout from '../../components/AdminLayout';
import { 
  Users, 
  UserCheck, 
  FileText, 
  Clock, 
  Eye,
  Plus,
  ArrowRight
} from 'lucide-react';

interface Stats {
  users: number;
  physiotherapists: number;
  blog_posts: number;
  sessions: number;
  page_views: number;
}

interface User {
  id: number;
  email_address: string;
  created_at: string;
}

interface Physiotherapist {
  id: number;
  first_name: string;
  last_name: string;
  created_at: string;
}

interface BlogPost {
  id: number;
  title: string;
  author: string;
  created_at: string;
}

interface Props {
  stats: Stats;
  recent_users: User[];
  recent_physiotherapists: Physiotherapist[];
  recent_blog_posts: BlogPost[];
}

const AdminDashboard: React.FC<Props> = ({ 
  stats, 
  recent_users, 
  recent_physiotherapists, 
  recent_blog_posts 
}) => {
  const statCards = [
    {
      title: 'Utilisateurs',
      value: stats.users,
      icon: Users,
      color: 'bg-blue-500',
      href: '/admin/users'
    },
    {
      title: 'Kinésithérapeutes',
      value: stats.physiotherapists,
      icon: UserCheck,
      color: 'bg-green-500',
      href: '/admin/physiotherapists'
    },
    {
      title: 'Articles',
      value: stats.blog_posts,
      icon: FileText,
      color: 'bg-purple-500',
      href: '/admin/blog_posts'
    },
    {
      title: 'Sessions',
      value: stats.sessions,
      icon: Clock,
      color: 'bg-orange-500',
      href: '/admin/sessions'
    },
    {
      title: 'Page Views',
      value: stats.page_views,
      icon: Eye,
      color: 'bg-red-500',
      href: '/admin/page_views'
    }
  ];

  return (
    <AdminLayout title="Dashboard">
      <Head title="Dashboard Admin" />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Admin</h1>
            <p className="text-gray-600">Vue d'ensemble de votre application</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {statCards.map((stat) => (
            <Card key={stat.title} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {stat.title}
                </CardTitle>
                <stat.icon className={`h-4 w-4 ${stat.color.replace('bg-', 'text-')}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <Link 
                  href={stat.href}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
                >
                  Voir tous <ArrowRight className="h-3 w-3 ml-1" />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Data */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Users */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Utilisateurs récents</CardTitle>
                <Link href="/admin/users/new">
                  <Button size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recent_users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{user.email_address}</p>
                      <p className="text-sm text-gray-500">
                        Créé le {new Date(user.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Link href={`/admin/users/${user.id}`}>
                      <Button size="sm" variant="ghost">
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Physiotherapists */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Kinésithérapeutes récents</CardTitle>
                <Link href="/admin/physiotherapists/new">
                  <Button size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recent_physiotherapists.map((physio) => (
                  <div key={physio.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">
                        {physio.first_name} {physio.last_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        Créé le {new Date(physio.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Link href={`/admin/physiotherapists/${physio.id}`}>
                      <Button size="sm" variant="ghost">
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Blog Posts */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Articles récents</CardTitle>
                <Link href="/admin/blog_posts/new">
                  <Button size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recent_blog_posts.map((post) => (
                  <div key={post.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{post.title}</p>
                      <p className="text-sm text-gray-500">
                        Par {post.author} • {new Date(post.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Link href={`/admin/blog_posts/${post.id}`}>
                      <Button size="sm" variant="ghost">
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard; 
